import type { ZxcvbnResult } from '@zxcvbn-ts/core'

type ZxcvbnFactoryInstance = InstanceType<
  typeof import('@zxcvbn-ts/core')['ZxcvbnFactory']
>

let factoryPromise: Promise<ZxcvbnFactoryInstance> | null = null

/**
 * Loads the sizeable dictionaries only when the registration UI actually needs
 * them. Common + English are the complete ASCII-oriented upstream data set:
 * common passwords, English words/names, every bundled keyboard graph, l33t,
 * dates, repeats, sequences, separators, and word sequences.
 */
async function getFactories() {
  if (!factoryPromise) {
    factoryPromise = Promise.all([
      import('@zxcvbn-ts/core'),
      import('@zxcvbn-ts/language-common'),
      import('@zxcvbn-ts/language-en'),
    ]).then(([core, common, english]) => {
      const options = {
        translations: english.translations,
        graphs: common.adjacencyGraphs,
        dictionary: {
          ...common.dictionary,
          ...english.dictionary,
        },
        useLevenshteinDistance: true,
      }

      return new core.ZxcvbnFactory(options)
    })
  }
  return factoryPromise
}

/**
 * Estimates strength only; this function never decides whether a password may
 * be registered. Dynamic user inputs make passwords containing the current
 * username/email or the application name easier to score appropriately.
 */
export async function analyzePassword(
  password: string,
  userInputs: Array<string | number>,
): Promise<ZxcvbnResult> {
  const factory = await getFactories()
  return factory.check(password, userInputs)
}

export type { ZxcvbnResult } from '@zxcvbn-ts/core'

/**
 * Password policy enforced at registration, password change, and recovery
 * reset. Length is a cheap floor/ceiling checked ahead of zxcvbn; the
 * zxcvbn score is the real strength gate. Deliberately no character-class
 * (uppercase/digit/symbol) rules — those are satisfied by predictable
 * passwords while rejecting long, high-entropy passphrases.
 */
export const MIN_PASSWORD_LENGTH = 8
export const MAX_PASSWORD_LENGTH = 128
export const MIN_PASSWORD_SCORE = 3

/**
 * Returns a user-facing error string if `password` fails the policy, or
 * null if it passes. `score` is the zxcvbn score (0-4) already computed by
 * PasswordStrengthReport for this input, or null if not yet analyzed.
 */
export function getPasswordPolicyError(password: string, score: number | null): string | null {
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`
  }
  if (password.length > MAX_PASSWORD_LENGTH) {
    return `Password must be no more than ${MAX_PASSWORD_LENGTH} characters long.`
  }
  if (score === null || score < MIN_PASSWORD_SCORE) {
    return 'Please choose a stronger password (reach at least "Strong" on the meter above).'
  }
  return null
}
